import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { ShoppingCart, ChevronLeft } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import SaveDesignButton from '../components/SaveDesignButton';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import ProductPreview3D from '../components/ProductPreview3D';
import PhotoPrintPreview from '../components/PhotoPrintPreview';
import DesignTools, { COLOR_HEX_MAP } from './design/DesignTools';
import DesignCanvas from './design/DesignCanvas';

const DesignEditor = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const savedDesignId = searchParams.get('design');
  const editCartItemId = searchParams.get('edit');
  const { addToCart, updateCartItem, cart } = useCart();
  const { token } = useAuth();
  const canvasRef = useRef(null);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [designConfig, setDesignConfig] = useState({
    position_x: 50, position_y: 50, scale: 1, rotation: 0,
    text: '', text_font: 'Arial', text_color: '#000000',
    background_color: '#FFFFFF', placement_notes: ''
  });
  const [selectedColor, setSelectedColor] = useState(location.state?.color || '');
  const [selectedSize, setSelectedSize] = useState(location.state?.size || '');
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [orientation, setOrientation] = useState('portrait'); // 'portrait' | 'landscape'
  const [activeTab, setActiveTab] = useState('upload');

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await api.get(`/products/${productId}`);
        setProduct(response.data);
        if (response.data.colors?.length > 0 && !selectedColor) setSelectedColor(response.data.colors[0]);
        if (response.data.sizes?.length > 0 && !selectedSize) setSelectedSize(response.data.sizes[0]);
      } catch {
        toast.error('Kunde inte ladda produkten');
        navigate('/produkter');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
      setActiveTab('adjust');
      toast.success('Bild uppladdad!');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    maxFiles: 1, maxSize: 10 * 1024 * 1024
  });

  const handleConfigChange = (key, value) => setDesignConfig(prev => ({ ...prev, [key]: value }));
  const handleReset = () => setDesignConfig({ position_x: 50, position_y: 50, scale: 1, rotation: 0, text: '', text_font: 'Arial', text_color: '#000000', background_color: '#FFFFFF' });
  const handleClearImage = () => { setUploadedImage(null); setImagePreview(null); setActiveTab('upload'); };

  // Hydrate from saved design
  useEffect(() => {
    if (!savedDesignId || !token) return;
    (async () => {
      try {
        const res = await api.get(`/saved-designs/${savedDesignId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const c = res.data?.customization;
        if (!c || c.type !== 'design') return;
        if (c.uploaded_image_url) {
          const url = c.uploaded_image_url.startsWith('/api')
            ? `${process.env.REACT_APP_BACKEND_URL}${c.uploaded_image_url}`
            : c.uploaded_image_url;
          setImagePreview(url);
          setActiveTab('adjust');
        }
        if (c.color) setSelectedColor(c.color);
        if (c.size) setSelectedSize(c.size);
        setDesignConfig((prev) => ({
          ...prev,
          text: c.text || '',
          text_font: c.text_font || prev.text_font,
          text_color: c.text_color || prev.text_color,
          background_color: c.background_color || prev.background_color,
          position_x: c.position_x ?? prev.position_x,
          position_y: c.position_y ?? prev.position_y,
          scale: c.scale ?? prev.scale,
          rotation: c.rotation ?? prev.rotation,
          placement_notes: c.placement_notes || '',
        }));
      } catch { toast.error('Kunde inte ladda sparad design'); }
    })();
  }, [savedDesignId, token]);

  // Hydrate from cart item (when customer clicks "Edit" in cart)
  useEffect(() => {
    if (!editCartItemId || !cart?.items) return;
    const cartItem = cart.items.find((i) => i.cart_item_id === editCartItemId);
    if (!cartItem) return;
    const c = cartItem.customization || {};
    if (c.uploaded_image_url) {
      const url = c.uploaded_image_url.startsWith('/api')
        ? `${process.env.REACT_APP_BACKEND_URL}${c.uploaded_image_url}`
        : c.uploaded_image_url;
      setImagePreview(url);
      setActiveTab('adjust');
    }
    if (c.color || cartItem.color) setSelectedColor(c.color || cartItem.color);
    if (c.size || cartItem.size) setSelectedSize(c.size || cartItem.size);
    if (c.frame) setSelectedFrame(c.frame);
    if (c.orientation || cartItem.print_orientation) setOrientation(c.orientation || cartItem.print_orientation);
    setDesignConfig((prev) => ({
      ...prev,
      text: c.text || '',
      text_font: c.text_font || prev.text_font,
      text_color: c.text_color || prev.text_color,
      background_color: c.background_color || prev.background_color,
      position_x: c.position_x ?? prev.position_x,
      position_y: c.position_y ?? prev.position_y,
      scale: c.scale ?? prev.scale,
      rotation: c.rotation ?? prev.rotation,
      placement_notes: c.placement_notes || '',
    }));
  }, [editCartItemId, cart?.items]);

  const buildSavedDesignPayload = async () => {
    if (!imagePreview) { toast.error('Ladda upp en bild först'); return null; }
    let uploadedImageUrl;
    if (imagePreview.startsWith('data:')) {
      const uploadRes = await api.post('/upload-base64', { image: imagePreview });
      uploadedImageUrl = uploadRes.data.url;
    } else {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      uploadedImageUrl = imagePreview.startsWith(backendUrl)
        ? imagePreview.replace(backendUrl, '')
        : imagePreview;
    }
    let resolvedPrice = product?.price;
    let printSize = null;
    let printQuality = null;
    const hasAdminSizePricing = product?.available_sizes?.length > 0 && product?.size_quality_prices?.length > 0;
    if (hasAdminSizePricing && selectedSize) {
      const quality = product?.available_qualities?.[0] || 'Standard';
      const entry = product.size_quality_prices.find((p) => p.size === selectedSize && (p.quality || 'Standard') === quality);
      if (entry) { resolvedPrice = entry.price; printSize = selectedSize; printQuality = quality; }
    }
    // Add frame add-on price
    if (selectedFrame && product?.has_frame_option) {
      resolvedPrice = (resolvedPrice || 0) + (Number(selectedFrame.price) || 0);
    }
    return {
      editor_type: 'design',
      product_id: productId,
      product_name: product?.name,
      price: resolvedPrice,
      quantity: 1,
      print_size: printSize,
      print_quality: printQuality,
      print_orientation: product?.supports_orientation ? orientation : null,
      color: selectedColor || null,
      size: selectedSize || null,
      image: product?.images?.[0],
      design_preview: uploadedImageUrl,
      customization: {
        type: 'design', uploaded_image_url: uploadedImageUrl,
        text: designConfig.text || null, text_font: designConfig.text_font || null,
        text_color: designConfig.text_color || null, background_color: designConfig.background_color || null,
        position_x: designConfig.position_x, position_y: designConfig.position_y,
        scale: designConfig.scale, rotation: designConfig.rotation,
        placement_notes: designConfig.placement_notes || null,
        color: selectedColor || null, size: selectedSize || null,
        orientation: product?.supports_orientation ? orientation : null,
        frame: selectedFrame || null,
      },
    };
  };

  const handleAddToCart = async () => {
    try {
      let uploadedImageUrl = null;
      if (imagePreview) {
        toast.info('Laddar upp bild...');
        const uploadRes = await api.post('/upload-base64', { image: imagePreview });
        uploadedImageUrl = uploadRes.data.url;
      }
      // Resolve price from admin size/quality matrix if applicable
      let resolvedPrice = product?.price;
      let printSize = null;
      let printQuality = null;
      const hasAdminSizePricing = product?.available_sizes?.length > 0 && product?.size_quality_prices?.length > 0;
      if (hasAdminSizePricing && selectedSize) {
        const quality = product?.available_qualities?.[0] || 'Standard';
        const entry = product.size_quality_prices.find(p => p.size === selectedSize && (p.quality || 'Standard') === quality);
        if (entry) {
          resolvedPrice = entry.price;
          printSize = selectedSize;
          printQuality = quality;
        }
      }
      // Add frame add-on price
      if (selectedFrame && product?.has_frame_option) {
        resolvedPrice = (resolvedPrice || 0) + (Number(selectedFrame.price) || 0);
      }
      const payload = {
        product_id: productId, name: product?.name, price: resolvedPrice, quantity: 1,
        color: selectedColor || null, size: selectedSize || null,
        print_size: printSize, print_quality: printQuality,
        print_orientation: product?.supports_orientation ? orientation : null,
        image: product?.images?.[0], design_preview: uploadedImageUrl,
        customization: {
          type: 'design', uploaded_image_url: uploadedImageUrl,
          text: designConfig.text || null, text_font: designConfig.text_font || null,
          text_color: designConfig.text_color || null, background_color: designConfig.background_color || null,
          position_x: designConfig.position_x, position_y: designConfig.position_y,
          scale: designConfig.scale, rotation: designConfig.rotation,
          placement_notes: designConfig.placement_notes || null,
          color: selectedColor || null, size: selectedSize || null,
          orientation: product?.supports_orientation ? orientation : null,
          frame: selectedFrame || null,
        }
      };
      if (editCartItemId) {
        await updateCartItem(editCartItemId, payload);
        toast.success('Produkten uppdaterad!', { action: { label: 'Till kassan', onClick: () => navigate('/varukorg') } });
        navigate('/varukorg');
      } else {
        await addToCart(payload);
        toast.success('Tillagd i varukorgen!', { action: { label: 'Till kassan', onClick: () => navigate('/varukorg') } });
      }
    } catch { toast.error(editCartItemId ? 'Kunde inte uppdatera produkten' : 'Kunde inte lägga till i varukorgen'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner"></div></div>;

  const bgColor = COLOR_HEX_MAP[selectedColor] || designConfig.background_color;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="design-editor-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="container-main py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-primary" data-testid="back-button">
              <ChevronLeft className="w-5 h-5" /><span className="hidden sm:inline">Tillbaka</span>
            </button>
            <h1 className="font-semibold text-slate-900">Designa: {product?.name}</h1>
            <div className="flex items-center gap-2">
              <SaveDesignButton
                buildPayload={buildSavedDesignPayload}
                defaultName={`${product?.name || 'Design'}`}
                designId={savedDesignId}
                variant="outline"
                size="sm"
              />
              <Button size="sm" className="btn-primary text-sm py-2" onClick={handleAddToCart} data-testid="add-to-cart-design">
                <ShoppingCart className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Lägg i varukorg</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-main py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left - Previews */}
          <div className="order-2 lg:order-1 space-y-6">
            {selectedFrame?.image && (
              <div className="bg-white rounded-xl p-6 shadow-soft" data-testid="selected-frame-preview">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Vald ram: {selectedFrame.label}</h3>
                  <span className="text-sm font-mono text-primary">+{Number(selectedFrame.price || 0).toLocaleString('sv-SE')} kr</span>
                </div>
                <div className="aspect-square rounded-lg overflow-hidden bg-slate-50">
                  <img
                    src={selectedFrame.image.startsWith('http') ? selectedFrame.image : `${process.env.REACT_APP_BACKEND_URL}${selectedFrame.image}`}
                    alt={selectedFrame.label}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            )}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <h3 className="font-semibold text-slate-900 mb-4">Förhandsvisning</h3>
              <div className={`${product?.supports_orientation ? (orientation === 'landscape' ? 'aspect-[3/2]' : 'aspect-[2/3]') : 'aspect-square'} rounded-lg overflow-hidden`}>
                {product?.supports_orientation ? (
                  <PhotoPrintPreview texture={imagePreview} backgroundColor={bgColor} orientation={orientation} designConfig={designConfig} />
                ) : (
                  <ProductPreview3D modelType={product?.model_type} texture={imagePreview} color={bgColor} designConfig={designConfig} />
                )}
              </div>
              {!product?.supports_orientation && (
                <p className="text-center text-sm text-slate-400 mt-4">Dra för att rotera &bull; Scrolla för att zooma</p>
              )}
            </div>
            <DesignCanvas canvasRef={canvasRef} designConfig={designConfig} imagePreview={imagePreview} backgroundColor={bgColor} orientation={product?.supports_orientation ? orientation : undefined} />
          </div>

          {/* Right - Tools */}
          <div className="order-1 lg:order-2 space-y-6">
            {/* Orientation picker for photo prints */}
            {product?.supports_orientation && (
              <div className="bg-white rounded-xl p-5 shadow-soft" data-testid="orientation-picker">
                <h3 className="font-semibold text-slate-900 mb-3">Orientering</h3>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setOrientation('portrait')}
                    className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                      orientation === 'portrait' ? 'border-[#2a9d8f] bg-[#2a9d8f]/5' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="orientation-portrait"
                  >
                    <div className="w-8 h-12 rounded-sm border-2 border-current text-slate-600" />
                    <span className={`text-sm font-medium ${orientation === 'portrait' ? 'text-[#2a9d8f]' : 'text-slate-700'}`}>Stående</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOrientation('landscape')}
                    className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                      orientation === 'landscape' ? 'border-[#2a9d8f] bg-[#2a9d8f]/5' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="orientation-landscape"
                  >
                    <div className="w-12 h-8 rounded-sm border-2 border-current text-slate-600" />
                    <span className={`text-sm font-medium ${orientation === 'landscape' ? 'text-[#2a9d8f]' : 'text-slate-700'}`}>Liggande</span>
                  </button>
                </div>
              </div>
            )}
            <DesignTools
              activeTab={activeTab} setActiveTab={setActiveTab}
              designConfig={designConfig} onConfigChange={handleConfigChange}
              imagePreview={imagePreview} uploadedImage={uploadedImage}
              onClearImage={handleClearImage} onReset={handleReset}
              getRootProps={getRootProps} getInputProps={getInputProps} isDragActive={isDragActive}
              product={product} selectedColor={selectedColor} setSelectedColor={setSelectedColor}
              selectedSize={selectedSize} setSelectedSize={setSelectedSize}
              orientation={product?.supports_orientation ? orientation : undefined}
            />
            {product?.has_frame_option && (product?.frame_options || []).length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-soft" data-testid="frame-picker">
                <h3 className="font-semibold text-slate-900 mb-3">Lägg till ram (valfritt)</h3>
                <select
                  value={selectedFrame?.id || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) { setSelectedFrame(null); return; }
                    const found = product.frame_options.find((f) => f.id === id);
                    setSelectedFrame(found || null);
                  }}
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#2a9d8f]"
                  data-testid="frame-picker-select"
                >
                  <option value="">Ingen ram (+0 kr)</option>
                  {product.frame_options.map((frame) => (
                    <option key={frame.id || frame.label} value={frame.id || frame.label}>
                      {frame.label} (+{Number(frame.price || 0).toLocaleString('sv-SE')} kr)
                    </option>
                  ))}
                </select>
                {selectedFrame?.image && (
                  <div className="mt-3 flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                    <img
                      src={selectedFrame.image.startsWith('http') ? selectedFrame.image : `${process.env.REACT_APP_BACKEND_URL}${selectedFrame.image}`}
                      alt={selectedFrame.label}
                      className="w-14 h-14 rounded object-cover border border-slate-200"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{selectedFrame.label}</p>
                      <p className="text-xs text-primary font-mono">+{Number(selectedFrame.price || 0).toLocaleString('sv-SE')} kr</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignEditor;
